import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/shared/api/client"
import { AUTH_STORAGE_KEYS } from "@/shared/config/auth"
import type { Post } from "@/shared/lib/types"

export interface ViewHistoryItem {
	id: string
	userId: string
	postId: string
	lastViewedAt: string
	viewCount: number
	post: Post
}

function isAuthenticated(): boolean {
	return !!sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
}

export const useRecordView = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (data: { postId: string }) => {
			const response = await apiClient.post("/view-history", {
				postId: data.postId,
			})
			return response.data
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["view-history"] })
		},
	})
}

export const useRecordMediaProgress = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (data: {
			postId: string
			mediaId: string
			videoProgress?: number
			videoDuration?: number
		}) => {
			const response = await apiClient.post("/view-progress", data)
			return response.data
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["view-progress", variables.postId, variables.mediaId],
			})
		},
	})
}

export const useRecentViews = (limit = 30) => {
	return useQuery<ViewHistoryItem[]>({
		queryKey: ["view-history", "recent", limit],
		queryFn: async () => {
			const response = await apiClient.get("/view-history/recent", {
				params: { limit },
			})
			return response.data
		},
		enabled: isAuthenticated(),
	})
}

export interface ContinueWatchingItem {
	id: string
	postId: string
	mediaId: string
	videoProgress: number | null
	videoDuration: number | null
	updatedAt: string
	post: Post
}

export const useContinueWatching = (limit = 10) => {
	return useQuery<ContinueWatchingItem[]>({
		queryKey: ["view-history", "continue", limit],
		queryFn: async () => {
			const response = await apiClient.get("/view-history/continue", { params: { limit } })
			return response.data
		},
		enabled: isAuthenticated(),
	})
}

type ViewProgress = {
	videoProgress?: number | null
	videoDuration?: number | null
}

export const useViewProgress = (postId: string, mediaId?: string) => {
	return useQuery({
		queryKey: ["view-progress", postId, mediaId ?? null],
		queryFn: async () => {
			if (!mediaId) return null
			const response = await apiClient.get<ViewProgress | null>("/view-progress", {
				params: { postId, mediaId },
			})
			return response.data
		},
		enabled: !!postId && !!mediaId,
	})
}
